import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAdminAuth } from "../contexts/AdminAuthContext";
import { adminApi, formatApiError } from "../lib/api";
import AdminHeader from "../components/AdminHeader";
import {
  Search,
  Trash2,
  Check,
  X,
  Sparkles,
  Loader2,
  MessagesSquare,
  User,
  CalendarClock,
  Eye,
  EyeOff,
  Send,
  Filter,
} from "lucide-react";

const STATUS_LIST = [
  { id: "all", label: "الكل" },
  { id: "pending", label: "معلقة" },
  { id: "approved", label: "معتمدة" },
  { id: "implemented", label: "تم التنفيذ" },
  { id: "rejected", label: "مرفوضة" },
];

const STATUS_META = {
  pending: {
    label: "معلقة",
    pill: "bg-sky-500/15 border-sky-500/40 text-sky-200",
  },
  approved: {
    label: "معتمدة",
    pill: "bg-emerald-500/15 border-emerald-500/40 text-emerald-200",
  },
  implemented: {
    label: "تم التنفيذ",
    pill: "bg-[#C9A96A]/15 border-[#C9A96A]/40 text-[#E5CB94]",
  },
  rejected: {
    label: "مرفوضة",
    pill: "bg-red-500/15 border-red-500/40 text-red-200",
  },
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("ar-EG", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

// -----------------------------------------------------------------------
// بطاقة اقتراح داخل لوحة الإدارة
// -----------------------------------------------------------------------
const AdminSuggestionCard = ({ item, onUpdate, onDelete }) => {
  const [reply, setReply] = useState(item.admin_reply || "");
  const [savingReply, setSavingReply] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const meta = STATUS_META[item.status] || STATUS_META.pending;

  const handleAction = async (patch) => {
    await onUpdate(item.id, patch);
  };

  const handleSaveReply = async () => {
    setSavingReply(true);
    await onUpdate(item.id, { admin_reply: reply.trim() || null });
    setSavingReply(false);
  };

  return (
    <article
      data-testid={`admin-suggestion-${item.id}`}
      className="rounded-2xl bg-gradient-to-br from-white/[0.04] to-white/[0.01] border border-white/10 p-4 sm:p-5 flex flex-col gap-3"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span
          className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full border ${meta.pill}`}
        >
          {meta.label}
        </span>
        <div className="flex items-center gap-3 text-white/40 text-[11px]">
          <span className="flex items-center gap-1">
            <User className="w-3 h-3" />
            {item.name?.trim() || "زائر"}
          </span>
          <span className="flex items-center gap-1">
            <CalendarClock className="w-3 h-3" />
            {formatDate(item.created_at)}
          </span>
          <span className="flex items-center gap-1">
            {item.is_public ? (
              <Eye className="w-3 h-3 text-emerald-300" />
            ) : (
              <EyeOff className="w-3 h-3 text-white/40" />
            )}
            {item.is_public ? "منشور" : "مخفي"}
          </span>
        </div>
      </div>

      <p className="text-white/85 text-sm leading-relaxed whitespace-pre-wrap break-words">
        {item.content}
      </p>

      {/* Reply */}
      <div>
        <label className="text-white/70 text-[11px] font-bold flex items-center gap-1 mb-1">
          <MessagesSquare className="w-3 h-3" />
          رد الإدارة (اختياري)
        </label>
        <div className="flex flex-col sm:flex-row gap-2">
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value.slice(0, 500))}
            rows={2}
            placeholder="اكتب ردًا يظهر تحت الاقتراح للعامة."
            className="flex-1 rounded-xl bg-white/[0.03] border border-white/10 text-white px-3 py-2 text-xs placeholder:text-white/30 focus:outline-none focus:border-[#C9A96A]/50 resize-y min-h-[52px]"
          />
          <button
            type="button"
            data-testid={`admin-save-reply-${item.id}`}
            onClick={handleSaveReply}
            disabled={savingReply || (reply || "") === (item.admin_reply || "")}
            className="inline-flex items-center justify-center gap-1.5 h-9 sm:h-auto px-3 rounded-lg bg-[#C9A96A]/15 hover:bg-[#C9A96A]/25 border border-[#C9A96A]/30 text-[#E5CB94] font-bold text-[11px] transition disabled:opacity-40"
          >
            {savingReply ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Send className="w-3 h-3" />
            )}
            حفظ الرد
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-white/5">
        {item.status !== "approved" && (
          <button
            type="button"
            data-testid={`admin-approve-${item.id}`}
            onClick={() => handleAction({ status: "approved", is_public: true })}
            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/40 text-emerald-200 text-[11px] font-bold transition"
          >
            <Check className="w-3 h-3" />
            اعتماد
          </button>
        )}
        {item.status !== "rejected" && (
          <button
            type="button"
            data-testid={`admin-reject-${item.id}`}
            onClick={() => handleAction({ status: "rejected", is_public: false })}
            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg bg-red-500/15 hover:bg-red-500/25 border border-red-500/40 text-red-200 text-[11px] font-bold transition"
          >
            <X className="w-3 h-3" />
            رفض
          </button>
        )}
        {item.status !== "implemented" && (
          <button
            type="button"
            data-testid={`admin-implement-${item.id}`}
            onClick={() =>
              handleAction({ status: "implemented", is_public: true })
            }
            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg bg-[#C9A96A]/15 hover:bg-[#C9A96A]/25 border border-[#C9A96A]/40 text-[#E5CB94] text-[11px] font-bold transition"
          >
            <Sparkles className="w-3 h-3" />
            تم التنفيذ
          </button>
        )}
        <button
          type="button"
          data-testid={`admin-toggle-public-${item.id}`}
          onClick={() => handleAction({ is_public: !item.is_public })}
          className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/80 text-[11px] font-bold transition"
        >
          {item.is_public ? (
            <>
              <EyeOff className="w-3 h-3" />
              إخفاء
            </>
          ) : (
            <>
              <Eye className="w-3 h-3" />
              نشر
            </>
          )}
        </button>

        <div className="ms-auto">
          {confirmDelete ? (
            <div className="flex items-center gap-2">
              <span className="text-red-200 text-[11px]">حذف نهائي؟</span>
              <button
                type="button"
                data-testid={`admin-delete-confirm-${item.id}`}
                onClick={() => {
                  setConfirmDelete(false);
                  onDelete(item.id);
                }}
                className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-[11px] font-bold transition"
              >
                <Trash2 className="w-3 h-3" />
                نعم، احذف
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg bg-white/[0.04] border border-white/10 text-white/70 text-[11px] font-bold transition"
              >
                إلغاء
              </button>
            </div>
          ) : (
            <button
              type="button"
              data-testid={`admin-delete-${item.id}`}
              onClick={() => setConfirmDelete(true)}
              className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg bg-white/[0.04] hover:bg-red-500/15 border border-white/10 hover:border-red-500/40 text-white/60 hover:text-red-200 text-[11px] font-bold transition"
            >
              <Trash2 className="w-3 h-3" />
              حذف
            </button>
          )}
        </div>
      </div>
    </article>
  );
};

// -----------------------------------------------------------------------
// الصفحة الرئيسية للوحة تحكم الاقتراحات
// -----------------------------------------------------------------------
const AdminSuggestionsPage = () => {
  const { loading, isAuthenticated } = useAdminAuth();

  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState("");

  const fetchList = useCallback(async () => {
    setFetching(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (search.trim()) params.set("search", search.trim());
      const { data } = await adminApi.get(
        `/admin/suggestions${params.toString() ? "?" + params.toString() : ""}`
      );
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setFetching(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    if (isAuthenticated) fetchList();
  }, [isAuthenticated, fetchList]);

  const handleUpdate = async (id, patch) => {
    try {
      const { data } = await adminApi.patch(`/admin/suggestions/${id}`, patch);
      // update in place
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...data } : it)));
      // refresh counts
      fetchList();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminApi.delete(`/admin/suggestions/${id}`);
      setItems((prev) => prev.filter((it) => it.id !== id));
      fetchList();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const displayItems = useMemo(() => items, [items]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A1128] flex items-center justify-center text-white/50 text-sm">
        جارٍ التحقّق من الجلسة...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div className="min-h-screen bg-[#0A1128] text-white" dir="rtl">
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.04] z-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      <div className="relative z-10">
        <AdminHeader title="لوحة الاقتراحات" />

        <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-5">
          {/* Filters */}
          <div className="rounded-2xl bg-white/[0.02] border border-white/10 p-3 sm:p-4 flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Filter className="w-4 h-4 text-[#C9A96A]" />
              {STATUS_LIST.map((s) => {
                const active = statusFilter === s.id;
                const count = s.id === "all" ? counts.all : counts[s.id];
                return (
                  <button
                    key={s.id}
                    type="button"
                    data-testid={`admin-filter-${s.id}`}
                    onClick={() => setStatusFilter(s.id)}
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-bold border transition-colors ${
                      active
                        ? "bg-[#C9A96A]/15 border-[#C9A96A]/40 text-[#E5CB94]"
                        : "bg-white/[0.02] border-white/10 text-slate-300 hover:border-[#C9A96A]/25"
                    }`}
                  >
                    {s.label}
                    {typeof count === "number" && (
                      <span
                        className={`ms-1.5 px-1.5 py-0.5 rounded-md text-[9px] ${
                          active
                            ? "bg-[#0A1128]/50 text-[#E5CB94]"
                            : "bg-white/[0.04] text-slate-400"
                        }`}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="relative">
              <Search className="w-4 h-4 absolute start-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="admin-search-input"
                placeholder="بحث في الاقتراحات..."
                className="w-full h-10 rounded-xl bg-white/[0.03] border border-white/10 text-white ps-9 pe-4 text-sm placeholder:text-white/30 focus:outline-none focus:border-[#C9A96A]/50"
              />
            </div>
          </div>

          {/* Body */}
          {error && (
            <div className="rounded-xl bg-red-500/10 border border-red-500/30 text-red-200 text-sm p-3">
              {error}
            </div>
          )}

          {fetching && items.length === 0 ? (
            <div className="rounded-2xl bg-white/[0.02] border border-white/10 text-slate-400 text-center py-16 text-sm">
              جارٍ التحميل...
            </div>
          ) : displayItems.length === 0 ? (
            <div className="rounded-2xl bg-white/[0.02] border border-white/10 text-center py-16 text-sm text-white/60">
              لا توجد اقتراحات مطابقة للفلتر الحالي.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:gap-4">
              {displayItems.map((it) => (
                <AdminSuggestionCard
                  key={it.id}
                  item={it}
                  onUpdate={handleUpdate}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminSuggestionsPage;
